import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle2, Lock, RefreshCw, Scale, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtNum = (s) => {
  const n = Number(s);
  if (Number.isNaN(n)) return s;
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const typeColor = {
  ASSET:            'bg-blue-50 text-blue-700 border-blue-200',
  LIABILITY:        'bg-orange-50 text-orange-700 border-orange-200',
  REVENUE:          'bg-emerald-50 text-emerald-700 border-emerald-200',
  EXPENSE:          'bg-rose-50 text-rose-700 border-rose-200',
  CLEARING:         'bg-slate-50 text-slate-700 border-slate-200',
  PROVIDER_BALANCE: 'bg-violet-50 text-violet-700 border-violet-200',
  COMMISSION:       'bg-amber-50 text-amber-700 border-amber-200',
  TREASURY:         'bg-indigo-50 text-indigo-700 border-indigo-200',
  CUSTOMER_FUNDS:   'bg-cyan-50 text-cyan-700 border-cyan-200',
};

const FinanceTrialBalancePage = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ rows: [], totals: { debit: '0', credit: '0', diff: '0' } });
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('all');

  const fetchTB = async () => {
    setLoading(true);
    try {
      const params = currency !== 'all' ? { currency } : {};
      const r = await axios.get(`${API}/finance/trial-balance`, { headers: getAuthHeaders(), params });
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTB(); /* eslint-disable-next-line */ }, [currency]);

  // Compute per-currency totals if no currency filter (to validate balance per currency too).
  const perCurrencyTotals = useMemo(() => {
    const acc = {};
    (data.rows || []).forEach((r) => {
      const c = r.currency;
      if (!acc[c]) acc[c] = { debit: 0, credit: 0 };
      acc[c].debit += Number(r.debit);
      acc[c].credit += Number(r.credit);
    });
    return acc;
  }, [data.rows]);

  const overallDiff = Number(data.totals?.diff || 0);
  const balanced = Math.abs(overallDiff) < 0.005;

  return (
    <div className="space-y-6" data-testid="finance-trial-balance-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Scale className="w-6 h-6 text-indigo-600" />
            Balance de Comprobación
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Suma de débitos y créditos por cuenta. En un libro mayor sano, total débitos = total créditos por moneda.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 gap-1">
            <Lock className="w-3 h-3" /> Read-only
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1">
            <ShieldCheck className="w-3 h-3" /> Append-only
          </Badge>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="w-32" data-testid="tb-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="UYU">UYU</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={fetchTB} data-testid="tb-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      {/* Per-currency balance check cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(perCurrencyTotals).map(([cur, t]) => {
          const diff = t.debit - t.credit;
          const ok = Math.abs(diff) < 0.005;
          return (
            <Card
              key={cur}
              className={`border-0 shadow-sm p-4 ${ok ? '' : 'ring-2 ring-rose-300'}`}
              data-testid={`tb-currency-card-${cur}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{cur}</span>
                {ok ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Balanceado
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 gap-1">
                    <AlertTriangle className="w-3 h-3" /> Mismatch
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Débito</p>
                  <p className="font-mono font-semibold text-blue-700">{fmtNum(t.debit)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Crédito</p>
                  <p className="font-mono font-semibold text-amber-700">{fmtNum(t.credit)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500 uppercase">Diferencia</p>
                  <p className={`font-mono font-semibold ${ok ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtNum(diff)}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-left">
                  <th className="p-3 font-medium text-slate-600">Código</th>
                  <th className="p-3 font-medium text-slate-600">Cuenta</th>
                  <th className="p-3 font-medium text-slate-600">Tipo</th>
                  <th className="p-3 font-medium text-slate-600">Moneda</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Total Débito</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Total Crédito</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Balance Natural</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r) => (
                  <tr key={r.account_code} className="border-b hover:bg-slate-50" data-testid={`tb-row-${r.account_code}`}>
                    <td className="p-3 font-mono text-xs text-slate-800">{r.account_code}</td>
                    <td className="p-3 font-medium text-slate-800">{r.account_name}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${typeColor[r.account_type] || 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                        {r.account_type}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{r.currency}</td>
                    <td className="p-3 text-right font-mono text-blue-700">{fmtNum(r.debit)}</td>
                    <td className="p-3 text-right font-mono text-amber-700">{fmtNum(r.credit)}</td>
                    <td className={`p-3 text-right font-mono font-semibold ${Number(r.balance) < 0 ? 'text-rose-700' : 'text-slate-800'}`}>
                      {fmtNum(r.balance)}
                    </td>
                  </tr>
                ))}
                {(data.rows || []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-12 text-center text-slate-400">Sin cuentas para mostrar</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                  <td colSpan={4} className="p-3 text-right text-slate-700">TOTAL (global)</td>
                  <td className="p-3 text-right font-mono text-blue-700">{fmtNum(data.totals?.debit)}</td>
                  <td className="p-3 text-right font-mono text-amber-700">{fmtNum(data.totals?.credit)}</td>
                  <td className={`p-3 text-right font-mono ${balanced ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {balanced ? '✓ 0.00' : `Δ ${fmtNum(data.totals?.diff)}`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {!balanced && (
        <Card className="border-0 shadow-sm p-4 bg-rose-50 border-l-4 border-rose-400" data-testid="tb-mismatch-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div>
              <p className="font-semibold text-rose-800">Balance global descuadrado</p>
              <p className="text-sm text-rose-700 mt-1">
                Débitos − Créditos = <span className="font-mono">{fmtNum(data.totals?.diff)}</span>.
                Esto puede deberse a transacciones cruzadas entre monedas — revisa los totales por moneda arriba.
                Recuerda: nunca se ajusta manualmente, solo mediante una transacción de reverso o un nuevo asiento.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default FinanceTrialBalancePage;
